import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import { i18n } from 'i18n';
import { UnitSystem } from 'providers/api';
import { distanceMetricToImperial } from 'utils/unitConversions';
import { downloadCsv } from 'modules/project/ProjectAreaPage/ProjectReportsPage/reportCsv';
import ChartRenderer from '../../ProjectDetailsPage/ChartBuilder/ChartRenderer';
import { CategoryChartModel, MAX_SERIES } from '../../ProjectDetailsPage/ChartBuilder/chartSpec';
import { Model } from '../hooks/useModelState';
import { isSpoolVisible } from '../MineModel/levelAreaFilter';
import {
  bucketLabel,
  buildSpoolPivot,
  DATE_FIELDS,
  DateGranularity,
  LENGTH_MEASURES,
  MeasureFormat,
  MeasureTotals,
  pivotChartSeries,
  PivotLayout,
  pivotTable,
  spoolFieldLabel,
  SpoolField,
  spoolListTable,
  SpoolMeasure,
  toCsv,
  toTsv,
  visibleGroups
} from './spoolPivot';
import PivotFieldWells from './PivotFieldWells';

// Excel reads a CSV as the local code page unless it starts with a byte order mark.
const UTF8_BOM = String.fromCharCode(0xfeff);

interface SpoolReportDialogProps {
  open: boolean;
  onClose: () => void;
  model: Model;
}

const measureLabel = (measure: SpoolMeasure, unit: string): string => ({
  length: `${i18n._('Length')} (${unit})`,
  flowLoop: `${i18n._('Flow Loop')} (${unit})`,
  count: i18n._('Count'),
})[measure];

const formatNumber = (value: number, measure: SpoolMeasure): string => value.toLocaleString(undefined, {
  maximumFractionDigits: measure === 'count' ? 0 : 1,
});

const SpoolReportDialog = ({ open, onClose, model }: SpoolReportDialogProps) => {
  const [rows, setRows] = React.useState<SpoolField[]>(['installed', 'pipe']);
  const [column, setColumn] = React.useState<SpoolField | null>(null);
  const [measures, setMeasures] = React.useState<SpoolMeasure[]>(['length']);
  const [dateGranularity, setDateGranularity] = React.useState<DateGranularity>('year');
  const [shownOnly, setShownOnly] = React.useState(false);
  const [showChart, setShowChart] = React.useState(true);
  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
  const { enqueueSnackbar } = useSnackbar();

  const isImperial = model.project?.unitSystemPreference === UnitSystem.Imperial;
  const unit = isImperial ? 'ft' : 'm';
  const config = { rows, column, measures, dateGranularity };
  const usesDates = [...rows, column].some((field) => !!field && DATE_FIELDS.includes(field));

  const spools = React.useMemo(() => Object.values(model.spools || {})
    .filter(Boolean)
    .filter((spool) => !shownOnly || isSpoolVisible(spool, model)),
  [model.spools, model.showInactiveSpools, model.levelAreaFilter, model.spoolDateFilter, shownOnly]);

  const pivot = React.useMemo(
    () => buildSpoolPivot(spools, { rows, column, measures, dateGranularity }),
    [spools, rows, column, measures, dateGranularity],
  );

  const format: MeasureFormat = {
    label: (measure) => measureLabel(measure, unit),
    value: (measure, totals?: MeasureTotals) => {
      const raw = totals?.[measure] ?? 0;
      const converted = isImperial && LENGTH_MEASURES.includes(measure) ? distanceMetricToImperial(raw) : raw;
      return Math.round(converted * 100) / 100;
    },
  };

  const chartMeasure = measures[0];
  const chartModel: CategoryChartModel | null = rows.length === 0 ? null : {
    kind: 'category',
    buckets: pivot.groups.map((group) => bucketLabel(group.field, group.value)),
    series: column
      ? pivotChartSeries(pivot, column, MAX_SERIES, (totals) => format.value(chartMeasure, totals))
      : [{ name: format.label(chartMeasure), values: pivot.groups.map((group) => format.value(chartMeasure, group.total)) }],
    xLabel: spoolFieldLabel(rows[0]),
    unit: chartMeasure === 'count' ? null : unit,
  };

  const fileStem = (model.project?.name ?? 'project').replace(/[^\w-]+/g, '-');

  const handleLayoutChange = (layout: PivotLayout) => {
    setCollapsed(new Set());
    setRows(layout.rows);
    setColumn(layout.column);
  };

  const toggleCollapsed = (key: string) => setCollapsed((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const handleCopy = () => {
    navigator.clipboard.writeText(toTsv(pivotTable(pivot, config, format)))
      .then(() => enqueueSnackbar(i18n._('Report copied to clipboard'), { variant: 'success' }))
      .catch(() => enqueueSnackbar(i18n._('The browser did not allow copying to the clipboard'), { variant: 'error' }));
  };

  const handleExportReport = () => downloadCsv(`${fileStem}-spool-report.csv`, UTF8_BOM + toCsv(pivotTable(pivot, config, format)));
  const handleExportList = () => downloadCsv(`${fileStem}-spools.csv`, UTF8_BOM + toCsv(spoolListTable(spools, format)));

  const valueCells = (cells: Record<string, MeasureTotals>, total: MeasureTotals) => [
    ...pivot.columns.flatMap((value) => measures.map((measure) => ({ key: `${value}-${measure}`, measure, totals: cells[value] }))),
    ...measures.map((measure) => ({ key: `total-${measure}`, measure, totals: total })),
  ].map(({ key, measure, totals }) => (
    <TableCell key={key} align="right">{totals ? formatNumber(format.value(measure, totals), measure) : ''}</TableCell>
  ));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{i18n._('Spool Report')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <PivotFieldWells
            layout={{ rows, column }}
            onLayoutChange={handleLayoutChange}
            measures={measures}
            onMeasuresChange={setMeasures}
            measureLabel={format.label}
          />
        </Box>
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 1, mt: 1.5,
        }}
        >
          {usesDates && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">{i18n._('Dates by')}</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                color="secondary"
                value={dateGranularity}
                onChange={(event, value: DateGranularity | null) => value && setDateGranularity(value)}
              >
                <ToggleButton value="year">{i18n._('Year')}</ToggleButton>
                <ToggleButton value="month">{i18n._('Month')}</ToggleButton>
                <ToggleButton value="day">{i18n._('Day')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <FormControlLabel
            control={<Checkbox size="small" color="secondary" checked={shownOnly} onChange={(event) => setShownOnly(event.target.checked)} />}
            label={i18n._('Only spools shown in the view')}
          />
          <FormControlLabel
            control={<Checkbox size="small" color="secondary" checked={showChart} onChange={(event) => setShowChart(event.target.checked)} />}
            label={i18n._('Chart')}
          />
        </Box>

        {spools.length === 0 ? (
          <Typography sx={{ mt: 3 }} color="text.secondary">{i18n._('No spools to report.')}</Typography>
        ) : (
          <>
            {showChart && chartModel && (
              <Box sx={{ height: 260, mt: 2 }}>
                <ChartRenderer model={chartModel} type="column" stacked={!!column} />
              </Box>
            )}
            <Box sx={{ mt: 2, maxHeight: 420, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  {column && (
                    <TableRow>
                      <TableCell />
                      {[...pivot.columns.map((value) => bucketLabel(column, value)), i18n._('Total')].map((label) => (
                        <TableCell key={label} align="center" colSpan={measures.length}>{label}</TableCell>
                      ))}
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>{rows.map(spoolFieldLabel).join(' / ')}</TableCell>
                    {[...pivot.columns, 'total'].flatMap((value) => measures.map((measure) => (
                      <TableCell key={`${value}-${measure}`} align="right">{format.label(measure)}</TableCell>
                    )))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleGroups(pivot.groups, collapsed).map((group) => (
                    <TableRow key={group.key} hover>
                      <TableCell sx={{ pl: 1 + group.depth * 3, fontWeight: group.children.length > 0 ? 600 : 400, whiteSpace: 'nowrap' }}>
                        {group.children.length > 0 ? (
                          <IconButton size="small" onClick={() => toggleCollapsed(group.key)} sx={{ mr: 0.5, p: 0.25 }}>
                            {collapsed.has(group.key) ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        ) : <Box component="span" sx={{ display: 'inline-block', width: 28 }} />}
                        {bucketLabel(group.field, group.value)}
                      </TableCell>
                      {valueCells(group.cells, group.total)}
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                    <TableCell>{i18n._('Grand Total')}</TableCell>
                    {valueCells(pivot.grandTotal.cells, pivot.grandTotal.total)}
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleCopy} disabled={spools.length === 0}>{i18n._('Copy')}</Button>
        <Button color="secondary" onClick={handleExportReport} disabled={spools.length === 0}>{i18n._('Export Report CSV')}</Button>
        <Button color="secondary" onClick={handleExportList} disabled={spools.length === 0}>{i18n._('Export Spool List CSV')}</Button>
        <Button color="secondary" onClick={onClose}>{i18n._('Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpoolReportDialog;
